import React, { Dispatch, SetStateAction, useCallback } from 'react';

import { Box, HStack, Icon, VStack } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { CardTypes, ICredit, IPaymentMethod, IRevaultInformation } from 'features/payment/types';
import { mapPaymentMethodCardTypes } from 'features/payment/utils';
import { GestureResponderEvent } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { IBaseProps } from '@rbi-ctg/frontend';
import RadioIndicator from 'components/radio-indicator';
import { useGetAndLogUserErrorMessage } from 'hooks/log-user-error-message';
import useDialogModal from 'hooks/use-dialog-modal';
import { emptyCartContext, useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive } from 'styles/constants';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import CashAppMethod from './cash-app-method';
import CashMethod from './cash-method';
import { cardIconRenderer } from './credit-card-icon';
import { PaymentNotAvailable } from './payment-not-available';
import PayPalMethod from './paypal-method';
import PrepaidMethodBalance from './prepaid-method-balance';
import {
  DeleteIconButton,
  MethodContainer,
  MethodType,
  MethodTypeInnerWrapper,
  MethodTypeWrapper,
  borderRadius,
} from './styled';
import { isCreditCardPaymentMethod } from './utils';
import VenmoMethod from './venmo-method';

export interface IPaymentMethodOptionProps extends IBaseProps {
  method: IPaymentMethod;
  selected?: boolean;
  onPress?: (fdAccountId: string) => void;
  showDefault?: boolean;
  removable?: boolean;
  readonly?: boolean;
  deletePaymentMethod?: (fdAccountId: string) => Promise<void>;
  hideDeleteIcon?: boolean;
  disableHover?: boolean;
  disableMethod?: boolean;
  hideBorder?: boolean;
  ariaLabelledby?: string;
  revaultInformation?: IRevaultInformation;
  fromCheckout?: boolean;
  isDefaultPaymentMethod?: boolean;
  fullWidthDescription?: boolean;
  collapsed?: boolean;
  isRoundedBorder?: boolean;
  isFlatList?: boolean;
  isDisabled?: boolean;
  isQuickPay?: boolean;
  isMasked?: boolean;
  setIsQuickPayMethodSelected?: Dispatch<SetStateAction<boolean>>;
}

function PaymentMethodOption({
  selected,
  method,
  onPress,
  showDefault,
  removable,
  deletePaymentMethod,
  disableHover = false,
  disableMethod = false,
  hideDeleteIcon = false,
  hideBorder = false,
  ariaLabelledby = 'payment-method-loads-with payment-method-label payment-method-card payment-method-alias payment-method-expirationDate',
  revaultInformation,
  fromCheckout = false,
  isDefaultPaymentMethod = false,
  fullWidthDescription = true,
  collapsed,
  isRoundedBorder = false,
  isFlatList = false,
  isDisabled = false,
  isQuickPay = false,
  isMasked = false,
  setIsQuickPayMethodSelected = noop,
}: IPaymentMethodOptionProps) {
  const router = useRouter();

  const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
  const accountAlias = method.credit?.alias || method.prepaid?.alias;
  const showDeleteButton = !hideDeleteIcon && removable;
  const textColor = disableMethod
    ? getDesignToken('text-disabled')
    : getDesignToken('text-default');
  const { isInvalid = false, isExpired = false } = revaultInformation ?? {};
  const isRevaultRequired = isInvalid || isExpired;

  // Checking is credit card different than Apple Pay and Google Pay
  const isCreditCardPayment = isCreditCardPaymentMethod(method);
  // Checking the payment method is available
  const isPaymentMethodNotAvailable = isCreditCardPayment && isRevaultRequired;
  // Setting particular styles when the payment is not available and is credit card
  const containerStyles = isPaymentMethodNotAvailable
    ? {
        backgroundColor: !isFlatList ? getDesignToken('background-inline-alert-error') : '',
        borderTopLeftRadius: isRoundedBorder ? borderRadius : 0,
        borderTopRightRadius: isRoundedBorder ? borderRadius : 0,
      }
    : {};

  const onDeleteMethod = useCallback(() => {
    if (deletePaymentMethod) {
      deletePaymentMethod(accountIdentifier);
    }
  }, [deletePaymentMethod, accountIdentifier]);

  const isInCart = useCartContext() !== emptyCartContext;

  const onClickRevault = useCallback(
    (e?: GestureResponderEvent) => {
      if (e) {
        e.stopPropagation();
      }

      router.navigate(
        `${routes.addCard}?accountToDelete=${accountIdentifier}&isInCart=${isInCart}`
      );
    },
    [accountIdentifier, isInCart, router]
  );

  const onClickMethod = useCallback(() => {
    if (disableMethod) {
      return;
    }

    if (isRevaultRequired) {
      if (isQuickPay) {
        setIsQuickPayMethodSelected(true);
      }

      return onClickRevault();
    }
    if (typeof onPress === 'function') {
      return onPress(accountIdentifier);
    }
  }, [
    disableMethod,
    isRevaultRequired,
    onPress,
    isQuickPay,
    onClickRevault,
    setIsQuickPayMethodSelected,
    accountIdentifier,
  ]);

  const [DeletionConfirmationDialog, openDeletionConfirmationDialog] = useDialogModal({
    onConfirm: onDeleteMethod,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Delete payment method',
  });

  const openPaymentDeletionConfirmation = useCallback(() => {
    return openDeletionConfirmationDialog();
  }, [openDeletionConfirmationDialog]);

  const formatExpirationDate = (credit: ICredit) =>
    `${isExpired ? t('expired') : t('expire')} ${credit.expiryMonth}/${credit.expiryYear}`;

  const RenderMethodType = () => {
    if (method.credit) {
      const creditCardAlias = (
        <Typography color={textColor}>
          {t('endsIn').toLowerCase()} {method.credit.alias}
        </Typography>
      );
      let isCreditCard;
      let alias;
      let cardType;
      let backgroundColor = '';
      switch (method.credit.cardType) {
        case CardTypes.APPLE_PAY:
          isCreditCard = false;
          alias = t('applePay');
          break;
        case CardTypes.GOOGLE_PAY:
          isCreditCard = false;
          alias = t('googlePay');
          break;
        default:
          backgroundColor = containerStyles.backgroundColor || '';
          cardType = mapPaymentMethodCardTypes(method.credit.cardType);
          isCreditCard = true;
          alias = creditCardAlias;
      }
      return (
        <>
          {fullWidthDescription ? (
            <HStack flexWrap="wrap" space={1} flex={1}>
              <HStack backgroundColor={backgroundColor} alignItems="center" space={2}>
                {isCreditCard && (
                  <MethodType color={textColor} testID="payment-method-card">
                    {cardType || t('card')}
                  </MethodType>
                )}
                <MethodType color={textColor} textTransform="capitalize">
                  {alias}
                </MethodType>
                {isCreditCard && (
                  <Typography
                    color={textColor}
                    testID="payment-method-expirationDate"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {formatExpirationDate(method.credit)}
                  </Typography>
                )}
              </HStack>
            </HStack>
          ) : (
            <VStack justifyContent="center">
              <HStack space={2}>
                {isCreditCard && (
                  <MethodType color={textColor} testID="payment-method-card">
                    {cardType || t('card')}
                  </MethodType>
                )}
                <MethodType color={textColor} textTransform="capitalize">
                  {alias}
                </MethodType>
              </HStack>
              {isCreditCard && (
                <HStack>
                  <Typography
                    color={isDisabled ? primitive.$blackOpacity30 : textColor}
                    testID="payment-method-expirationDate"
                    fontSize="xs"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {formatExpirationDate(method.credit)}
                  </Typography>
                </HStack>
              )}
            </VStack>
          )}
        </>
      );
    }

    if (method.prepaid) {
      return (
        <PrepaidMethodBalance method={method} isDisabled={disableMethod} isMasked={isMasked} />
      );
    }

    if (method.cash) {
      return <CashMethod isDisabled={disableMethod} />;
    }

    if (method.paypal) {
      return <PayPalMethod isDisabled={disableMethod} />;
    }

    if (method.venmo) {
      return <VenmoMethod isDisabled={disableMethod} />;
    }

    if (method.cashapp) {
      return <CashAppMethod isDisabled={disableMethod} />;
    }

    return null;
  };

  const getPaymentMethodNotAvailableErrorMessage = useGetAndLogUserErrorMessage({
    message: t('paymentMethodNotAvailable'),
  });

  return (
    <>
      <MethodContainer
        disableHover={disableHover}
        disableMethod={disableMethod}
        hideBorder={hideBorder}
        aria-labelledby={ariaLabelledby}
        testID={`payment-method-${accountIdentifier}`}
        {...containerStyles}
      >
        <MethodTypeWrapper
          data-private
          onPress={onClickMethod}
          $isClickable={!!onPress}
          disableMethod={disableMethod}
          fromCheckout={fromCheckout}
          testID={`method-type-wrapper-${accountIdentifier}`}
          accessibilityRole="button"
        >
          <VStack flex={1}>
            <MethodTypeInnerWrapper testID="payment-method-types" alignItems="center">
              <Box justifyContent="center" marginRight={'$2'}>
                {method.credit && cardIconRenderer({ methodType: method.credit.cardType })}
                {method.prepaid && <Icon variant="giftCard" />}
                {method.cash && <Icon variant="cash" />}
                {method.paypal && cardIconRenderer({ methodType: CardTypes.PAYPAL })}
                {method.venmo && cardIconRenderer({ methodType: CardTypes.VENMO })}
                {method.cashapp && cardIconRenderer({ methodType: CardTypes.CASHAPP })}
              </Box>

              <RenderMethodType />
              {disableMethod && (
                <HStack alignItems="center">
                  <Icon
                    variant="error"
                    size="$5"
                    color={getDesignToken('text-error')}
                    mr="$1"
                    ml="$2"
                  />
                  <Typography color={getDesignToken('text-error')} variant="copyTwo">
                    {getPaymentMethodNotAvailableErrorMessage()}
                  </Typography>
                </HStack>
              )}
            </MethodTypeInnerWrapper>
          </VStack>
          {showDefault && (
            <RadioIndicator
              accessibilityLabel={accountAlias}
              isSelected={selected || isDefaultPaymentMethod}
              isDisabled={isPaymentMethodNotAvailable || disableMethod}
            />
          )}
        </MethodTypeWrapper>
        {showDeleteButton && (
          <Box width="$10" ml="$2">
            <DeleteIconButton
              testID="remove-card-button"
              accessibilityLabel={t('removePaymentMethod')}
              onPress={openPaymentDeletionConfirmation}
              icon={<Icon variant="remove" size="$7" />}
            />
          </Box>
        )}
      </MethodContainer>
      {isCreditCardPayment && isExpired && (
        <PaymentNotAvailable
          isExpired={isExpired}
          collapsed={collapsed}
          onPress={() => {
            if (isQuickPay) {
              setIsQuickPayMethodSelected(true);
            }
            onClickRevault();
          }}
          isQuickPay
        />
      )}
      {isCreditCardPayment && isRevaultRequired && !isExpired && (
        <PaymentNotAvailable
          collapsed={collapsed}
          onPress={() => {
            if (isQuickPay) {
              setIsQuickPayMethodSelected(true);
            }
            onClickRevault();
          }}
          isQuickPay
        />
      )}
      <DeletionConfirmationDialog
        testID="delete-confirmation-dialog"
        heading={t('deletePaymentMethod')}
        body={t('confirmDeletePaymentMethod')}
        modalAppearanceEventMessage="Confirmation: Delete Payment Method"
      />
    </>
  );
}

export default PaymentMethodOption;
